import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { requestNotificationPermission, onForegroundMessage, sendNotification } from '@/lib/messaging';

/**
 * Initialise push notifications for the logged-in user.
 * - Asks for permission once (stored in localStorage to avoid repeat prompts)
 * - Shows in-app toast for foreground messages
 */
export function useNotifications(userId) {
  const [permissionStatus, setPermissionStatus] = useState(
    'Notification' in window ? Notification.permission : 'unsupported'
  );

  // ── Request permission on first login ───────────────────────
  useEffect(() => {
    if (!userId) return;
    if (permissionStatus === 'unsupported') return;
    if (permissionStatus === 'denied') return;

    // Only ask once per browser (track with localStorage)
    const key = `fym_notif_asked_${userId}`;
    if (localStorage.getItem(key)) return;

    // Small delay so the UI settles before the browser prompt
    const timer = setTimeout(async () => {
      const token = await requestNotificationPermission(userId);
      // Only mark as "asked" when we successfully got a token
      // (so we retry next session if permission was denied or token failed)
      if (token) localStorage.setItem(key, '1');
      setPermissionStatus(Notification.permission);
    }, 2000);

    return () => clearTimeout(timer);
  }, [userId, permissionStatus]);

  // ── Foreground message → toast ───────────────────────────────
  useEffect(() => {
    const unsub = onForegroundMessage((payload) => {
      const title = payload.notification?.title || 'FLASHYOURMEME';
      const body  = payload.notification?.body  || '';

      toast(title, {
        description: body,
        duration: 6000,
        icon: '🔔',
      });
    });
    return unsub;
  }, []);

  // Re-request permission (call from a button)
  const requestPermission = async () => {
    const token = await requestNotificationPermission(userId);
    if (token) {
      localStorage.setItem(`fym_notif_asked_${userId}`, '1');
      setPermissionStatus('granted');
      toast.success('Notifications enabled');
    } else {
      toast.error('Notification permission denied or failed');
    }
    return token;
  };

  // Register token then send test notification (always re-registers to ensure token is fresh)
  const sendTestNotification = async () => {
    if (!userId) return;

    // Step 1: Register / refresh token
    toast.loading('Step 1: Registering token…', { id: 'notif-test' });
    const token = await requestNotificationPermission(userId);

    if (!token) {
      toast.error('Token registration failed — check VAPID key or browser permission', { id: 'notif-test' });
      console.error('[FCM Test] No token. VAPID key:', process.env.REACT_APP_FIREBASE_VAPID_KEY?.slice(0, 10) + '…');
      return;
    }

    toast.loading('Step 2: Sending notification…', { id: 'notif-test' });
    console.log('[FCM Test] Token ok, sending to userId:', userId);

    // Step 2: Send via API
    const res = await fetch('/api/notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'test',
        title: '🔔 Test Notification',
        body: 'Push notifications are working! ✅',
        targetUserIds: [userId],
        data: { type: 'test', url: '/' },
      }),
    });

    const json = await res.json().catch(() => ({}));
    console.log('[FCM Test] API response:', json);

    if (json.sent > 0) {
      toast.success(`Sent! Now minimize the app to see the banner`, { id: 'notif-test', duration: 8000 });
    } else if (json.sent === 0) {
      toast.error(`API reached but sent=0 — token may not match Firestore. Details: ${JSON.stringify(json)}`, { id: 'notif-test', duration: 10000 });
    } else {
      toast.error(`API error: ${JSON.stringify(json)}`, { id: 'notif-test', duration: 8000 });
    }
  };

  return { permissionStatus, requestPermission, sendTestNotification };
}
